import { React, useState, useEffect, useContext, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  DashCircle,
  Download,
  Copy,
  PlusCircle,
  Gear,
  Box,
  ChevronDown,
} from "react-bootstrap-icons";
import {
  Select,
  Popover,
  Flex,
  Spin,
  Alert,
  Input,
  InputNumber,
  Button,
  Collapse,
  Popconfirm,
  Divider,
  Radio,
  Space,
  Tooltip,
  message,
} from "antd";
import {
  CopyOutlined,
  CheckOutlined,
  DeleteOutlined,
  InfoCircleFilled,
  ArrowRightOutlined,
  InfoCircleTwoTone,
  LoadingOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  DownOutlined,
  RightOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import colors from "../../styles/_colors.scss";

import { TimezoneDropdown } from "../../components/elements/TimezoneDropdown";
import GoogleCloudConfigForm from "./GoogleCloudConfigFile";
import { TagValueAutoComplete } from "./TagValueAutoComplete";
import { useDirtyState } from "../../hooks/useDirtyState";
import { useTagNameOptions } from "../../hooks/useTagNameOptions";

import ConfigService from "../../services/config.service";
import copyIcon from "../../assets/copy-icon.svg";
import { SupportRequestLink } from "../../components/elements/SupportRequestLink";

const { Option } = Select;

const _ = require("lodash");

const checkEmptyArray = (arr) => {
  const check =
    !arr ||
    arr.length === 0 ||
    arr.filter((elem) => Object.keys(elem).length !== 0).length === 0;

  return check;
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text?.current?.value || "");
    return true;
  } catch (error) {
    return false;
  }
};

export const ProductSettingsModal = ({ context }) => {
  const {
    productDescLink,
    timezones,
    timezoneName,
    curTimezone,
    companyDomains,
    setConfigParams,
    setModalSaveStatus,
  } = useContext(context);

  const [choosenTimezone, setChoosenTimezone] = useState(curTimezone);
  const [curCompanyDomains, setCurCompanyDomains] = useState(companyDomains);
  const [domainOptions, setDomainOptions] = useState([]);
  const [domainOptionsLoading, setDomainOptionsLoading] = useState(true);

  const [configParamsToSave, setConfigParamsToSave] = useState({});

  const [showGuideDivDomain, setShowGuideDivDomain] = useState(false);

  useEffect(() => {
    setChoosenTimezone(curTimezone);
    setCurCompanyDomains(companyDomains);
  }, [curTimezone, companyDomains]);

  // Every domain seen in the tag data, so the user can pick one instead of
  // typing it blind. On failure the select still accepts free-text entries.
  useEffect(() => {
    let cancelled = false;
    ConfigService.getDomainNames()
      .then((resp) => resp.json())
      .then((data) => {
        if (!cancelled && data?.result) setDomainOptions(data.domains || []);
      })
      .catch((e) => console.error("Failed to load domains:", e))
      .finally(() => {
        if (!cancelled) setDomainOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTimezoneChange = (newTimezone) => setChoosenTimezone(newTimezone);

  // Keep the existing object for domains that stay selected, so fields like
  // tag_inactivity aren't lost on save.
  const handleDomainsChange = (values) =>
    setCurCompanyDomains(
      values.map(
        (value) =>
          curCompanyDomains.find((elem) => elem.domain === value) || {
            domain: value,
          },
      ),
    );

  const isDomainEmpty = (domain) => {
    return Object.values(domain).every((val) => !val || val.trim() === "");
  };

  const filterEmptyDomains = (domains) =>
    domains.filter((domain) => !isDomainEmpty(domain));

  useEffect(() => {
    setConfigParamsToSave((prevParams) => ({
      ...prevParams,
      timezone: choosenTimezone,
      company_domains: curCompanyDomains,
    }));
  }, [choosenTimezone, curCompanyDomains]);

  useDirtyState(
    { timezone: curTimezone, domains: filterEmptyDomains(companyDomains) },
    { timezone: choosenTimezone, domains: filterEmptyDomains(curCompanyDomains) },
    setModalSaveStatus,
  );

  useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);

  return (
    <div className="modal-elems-rows">
      <div>
        <h2 className="header-letter-spacing">General settings</h2>
      </div>
      <div className="container-guide modal-panel-button-row">
        <Link
          className="modal-guide-banner "
          target="_blank"
          to={productDescLink}
        >
          <div className="file-icon">
            <svg
              width="30"
              height="36"
              viewBox="0 0 30 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_d_190_157)">
                <path
                  d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z"
                  fill="#69C4FF"
                  fillOpacity="0.5"
                />
                <path
                  d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z"
                  fill="#69C4FF"
                  fillOpacity="0.5"
                />
                <path
                  d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z"
                  fill="#DBF1FF"
                />
                <path
                  d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z"
                  fill="#B0E0FF"
                />
                <path
                  d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z"
                  fill="white"
                />
                <line
                  x1="8.82252"
                  y1="9.40248"
                  x2="19.988"
                  y2="9.40248"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.90306"
                  y1="15.1451"
                  x2="20.0685"
                  y2="15.1451"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.82252"
                  y1="11.3499"
                  x2="19.988"
                  y2="11.3499"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.90306"
                  y1="17.0925"
                  x2="20.0685"
                  y2="17.0925"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.82252"
                  y1="13.1675"
                  x2="19.988"
                  y2="13.1675"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <path
                  d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z"
                  fill="#69C4FF"
                  fillOpacity="0.14"
                />
              </g>
              <defs>
                <filter
                  id="filter0_d_190_157"
                  x="0.255097"
                  y="0.170166"
                  width="24.9498"
                  height="30.5854"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="1.74496" />
                  <feGaussianBlur stdDeviation="0.872482" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_190_157"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_190_157"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </div>
          <div className="panel-button-text">
            <div className="panel-button-desc">
              Click here to follow the complete setup guide for the Tag Monitor.
              <br />
              Question not covered?{" "}
              <SupportRequestLink />
            </div>
          </div>
        </Link>
      </div>
      <div>
        <p
          style={{
            marginBottom: 0,
            maxWidth: "42rem",
            width: "100%",
            overflowWrap: "break-word",
          }}
        >
          Configure the settings below to start the configuration of the Tag
          Monitor. These are basic settings which will reflect on the complete
          implementation.
        </p>
      </div>
      <div style={{ position: "relative" }} className="elems-list">
        <h3 style={{ marginBottom: 0 }} className="modal-category">
          Domains
          <Tooltip title="Only the domain(s) added to this list will be activated for alerting and will be shown in the dashboards.">
            <InfoCircleFilled
              size={15}
              className="info-icon"
              style={{ marginLeft: "10px" }}
            />
          </Tooltip>
        </h3>
        <p style={{ marginBottom: 0 }}>
          Add the domain(s) for which you would like to activate the Tag Monitor
          in the list below.
        </p>
        <Select
          mode="tags"
          style={{ width: "100%", maxWidth: "30rem" }}
          placeholder="example.com"
          value={curCompanyDomains.map((elem) => elem.domain)}
          onChange={handleDomainsChange}
          options={domainOptions.map((domain) => ({
            label: domain,
            value: domain,
          }))}
          loading={domainOptionsLoading}
          allowClear
          tokenSeparators={[",", " "]}
          suffixIcon={<ChevronDown />}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          popupClassName="custom-dropdown"
        />
      </div>

      <TimezoneDropdown
        choosenTimezone={choosenTimezone}
        timezones={timezones}
        timezoneName={timezoneName}
        handleTimezoneChange={handleTimezoneChange}
      />
    </div>
  );
};

export const CSTSettingsModal = ({ context }) => {
  const {
    databaseName,
    CSImplmenetationLink,
    clientSideTemplateLink,
    clientErrorsThreshold,
    setConfigParams,
    setModalSaveStatus,
    gtmContainers,
    customNmbrCid,
    subscriptionModel,
    tags_monitoring_exclude_client,
    currentConfig,
  } = useContext(context);
  const navigate = useNavigate();

  const percentages = [1, 5, 10, 25, 50, 100];

  const [curThreshold, setCurThreshold] = useState(clientErrorsThreshold);
  const [configParamsToSave, setConfigParamsToSave] = useState({});
  const [showGuideDiv, setShowGuideDiv] = useState(false);
  const [maxNmbrCids, setMaxNmbrCids] = useState(false);
  const [trigger, setTrigger] = useState("");
  const [showGuideDivGTM, setShowGuideDivGTM] = useState(false);
  const [showGuideDivSensitivity, setShowGuideDivSensitivity] = useState(false);

  const {
    clientTags,
    loading: tagNamesLoading,
    error: tagNamesError,
  } = useTagNameOptions();

  // Alert deviation thresholds state - Client-side
  const [absoluteDeviation, setAbsoluteDeviation] = useState(
    currentConfig?.alert_absolute_deviation_client || 10,
  );
  const [relativeDeviation, setRelativeDeviation] = useState(
    currentConfig?.alert_relative_deviation_client || 50,
  );

  // Determine current sensitivity preset
  const determineAlertSensitivity = (abs, rel) => {
    if (abs === 5 && rel === 25) return "high";
    if (abs === 10 && rel === 50) return "medium";
    if (abs === 25 && rel === 100) return "low";
    return "custom";
  };

  const [alertSensitivity, setAlertSensitivity] = useState(
    determineAlertSensitivity(
      currentConfig?.alert_absolute_deviation_client || 10,
      currentConfig?.alert_relative_deviation_client || 50,
    ),
  );

  // Collapsible notification section state
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const [curGTMContainers, setCurGTMContainers] = useState(
    gtmContainers && gtmContainers.length
      ? gtmContainers
      : [{ type: "client-side" }],
  );

  const excludetagsby = ["tag name", "tag id"];
  const matchOptions = ["exactly matches", "matches regex", "contains"];
  const defaultType = excludetagsby[0];
  const defaultMatch = matchOptions[0];

  const normalizeTag = (tag) => ({
    type: tag?.type || defaultType,
    match: tag?.match || defaultMatch,
    value: tag?.value || "",
  });
  const inputRefDataset = useRef(null);
  const copyFeedbackTimeoutRef = useRef(null);
  const [isDatasetCopied, setIsDatasetCopied] = useState(false);

  const handleCopyDatasetName = async () => {
    const copied = await copyToClipboard(inputRefDataset);
    if (!copied) return;

    setIsDatasetCopied(true);
    if (copyFeedbackTimeoutRef.current) {
      clearTimeout(copyFeedbackTimeoutRef.current);
    }
    copyFeedbackTimeoutRef.current = setTimeout(
      () => setIsDatasetCopied(false),
      2000,
    );
  };

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const content = (
    <div>
      <p>
        You have reached the maximum number of containers
        allowed by your subscription.<br />
        To add more containers or upgrade your plan,
        please contact us via the
        <a
          target="blank"
          className="tooltip-link"
          style={{ color: "#cfff5e", textDecoration: 'none', cursor: 'pointer' }}
          href="https://code-cube.io/contact-us/"
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {" "}
          website
          {" "}
        </a>
        or
        <a
          target="blank"
          className="tooltip-link"
          style={{ color: "#cfff5e", textDecoration: 'none', cursor: 'pointer' }}
          href="https://portal.code-cube.io/support_request"
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {" "}
          support
        </a>
        .
      </p>
    </div>
  );

  const [tagsMonitoringExcludeClient, setTagsMonitoringExcludeClient] =
    useState(
      Array.isArray(tags_monitoring_exclude_client) &&
        tags_monitoring_exclude_client.length > 0
        ? tags_monitoring_exclude_client.map(normalizeTag)
        : [normalizeTag({})],
    );

  useEffect(() => {
    setTagsMonitoringExcludeClient(
      Array.isArray(tags_monitoring_exclude_client) &&
        tags_monitoring_exclude_client.length > 0
        ? tags_monitoring_exclude_client.map(normalizeTag)
        : [normalizeTag({})],
    );
  }, [tags_monitoring_exclude_client]);

  useEffect(() => {
    // Set the maximum number of containers per subscription model
    switch (customNmbrCid > 0) {
      case true:
        setMaxNmbrCids(customNmbrCid);
        break;
      default:
        switch (subscriptionModel) {
          case "essentials":
            setMaxNmbrCids(1);
            break;
          case "advanced":
            setMaxNmbrCids(3);
            break;
          case "premium":
          case "enterprise":
            setMaxNmbrCids(35);
            break;
          default:
            setMaxNmbrCids(1);
        }
    }
  }, [subscriptionModel]);

  useEffect(() => {
    setTrigger(curGTMContainers?.length >= maxNmbrCids ? "hover" : "");
  }, [maxNmbrCids, curGTMContainers]);

  const handleElemDeleteGTM = (idx) => {
    const newGtmContainerList = curGTMContainers.filter(
      (_, index) => index !== idx,
    );
    setCurGTMContainers(
      newGtmContainerList.length
        ? newGtmContainerList
        : [{ type: "client-side" }],
    );
  };

  const handleElemAddGTM = () => {
    if (curGTMContainers?.length < maxNmbrCids) {
      setCurGTMContainers([...curGTMContainers, { type: "client-side" }]);
    }
  };

  const handleGTMContainerInputValueChange = (idx, event, valueType) => {
    const newGTMContainerList = [...curGTMContainers];
    const elem = { ...newGTMContainerList[idx] };

    switch (valueType) {
      case "cidType":
        elem["type"] = event.target.value;
        break;
      case "gtmID":
        elem["container"] = event.target.value;
        elem["type"] = "client-side";
        break;
      default:
    }

    newGTMContainerList[idx] = elem;
    setCurGTMContainers(newGTMContainerList);
  };

  const handleElemAddExcludedClientTag = () => {
    setTagsMonitoringExcludeClient((prev) => [...prev, normalizeTag({})]);
  };

  const handleElemDeleteExcludedClientTag = (idx) => {
    setTagsMonitoringExcludeClient((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      return updated.length ? updated : [normalizeTag({})];
    });
  };

  const handleExcludedTagInputValue = (idx, field, newValue) => {
    setTagsMonitoringExcludeClient((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        [field]:
          newValue ??
          (field === "type"
            ? defaultType
            : field === "match"
              ? defaultMatch
              : ""),
      };
      return updated;
    });
  };

  const handleThresholdChange = (elem) => setCurThreshold(elem);

  useEffect(
    () => setCurThreshold(clientErrorsThreshold),
    [clientErrorsThreshold],
  );

  // Sync alert deviation values with currentConfig - Client-side
  useEffect(() => {
    const abs = currentConfig?.alert_absolute_deviation_client || 10;
    const rel = currentConfig?.alert_relative_deviation_client || 50;
    setAbsoluteDeviation(abs);
    setRelativeDeviation(rel);
    setAlertSensitivity(determineAlertSensitivity(abs, rel));
  }, [currentConfig]);

  // Handle sensitivity preset change
  const handleSensitivityChange = (value) => {
    setAlertSensitivity(value);
    if (value === "high") {
      setAbsoluteDeviation(5);
      setRelativeDeviation(25);
    } else if (value === "medium") {
      setAbsoluteDeviation(10);
      setRelativeDeviation(50);
    } else if (value === "low") {
      setAbsoluteDeviation(25);
      setRelativeDeviation(100);
    }
    // For "custom", keep current values
  };

  const isGTMContainerEmpty = (containerObj) => {
    return !containerObj.container || containerObj.container.trim() === "";
  };

  const filterEmptyGTMContainers = (containers) =>
    containers.filter((container) => !isGTMContainerEmpty(container));

  const isTagExclusionEmpty = (tag) => {
    return !tag.value || tag.value.trim() === "";
  };

  const filterEmptyTagExclusions = (tags) =>
    tags.filter((tag) => !isTagExclusionEmpty(tag));

  useEffect(() => {
    setConfigParamsToSave((prevParams) => ({
      ...prevParams,
      gtm_containers_client: filterEmptyGTMContainers(curGTMContainers),
      tags_monitoring_exclude_client: filterEmptyTagExclusions(
        tagsMonitoringExcludeClient,
      ),
      client_errors_threshold: curThreshold,
      alert_absolute_deviation_client: absoluteDeviation,
      alert_relative_deviation_client: relativeDeviation,
    }));
  }, [
    curGTMContainers,
    tagsMonitoringExcludeClient,
    curThreshold,
    absoluteDeviation,
    relativeDeviation,
  ]);

  useDirtyState(
    {
      gtm: filterEmptyGTMContainers(gtmContainers),
      // Normalize the baseline exactly like the live state (see useState above)
      // so unchanged tags don't read as different shapes.
      tags: filterEmptyTagExclusions(
        Array.isArray(tags_monitoring_exclude_client) &&
          tags_monitoring_exclude_client.length > 0
          ? tags_monitoring_exclude_client.map(normalizeTag)
          : [normalizeTag({})],
      ),
      threshold: Number(clientErrorsThreshold),
      absDev: Number(currentConfig?.alert_absolute_deviation_client || 10),
      relDev: Number(currentConfig?.alert_relative_deviation_client || 50),
    },
    {
      gtm: filterEmptyGTMContainers(curGTMContainers),
      tags: filterEmptyTagExclusions(tagsMonitoringExcludeClient),
      threshold: Number(curThreshold),
      absDev: Number(absoluteDeviation),
      relDev: Number(relativeDeviation),
    },
    setModalSaveStatus,
  );

  useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);

  return (
    <div className="modal-elems-rows">
      <div>
        <h2 className="header-letter-spacing">Client-side error monitoring setup</h2>
      </div>
      <div
        className="container-guide modal-panel-button-row"
        style={{ display: "flex", gap: "1em" }}
      >
        <Link
          className="modal-guide-banner "
          style={{ flex: 1 }}
          target="_blank"
          to={CSImplmenetationLink}
        >
          <div className="file-icon">
            <svg
              width="30"
              height="36"
              viewBox="0 0 30 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_d_190_157)">
                <path
                  d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z"
                  fill="#69C4FF"
                  fillOpacity="0.5"
                />
                <path
                  d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z"
                  fill="#69C4FF"
                  fillOpacity="0.5"
                />
                <path
                  d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z"
                  fill="#DBF1FF"
                />
                <path
                  d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z"
                  fill="#B0E0FF"
                />
                <path
                  d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z"
                  fill="white"
                />
                <line
                  x1="8.82252"
                  y1="9.40248"
                  x2="19.988"
                  y2="9.40248"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.90306"
                  y1="15.1451"
                  x2="20.0685"
                  y2="15.1451"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.82252"
                  y1="11.3499"
                  x2="19.988"
                  y2="11.3499"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.90306"
                  y1="17.0925"
                  x2="20.0685"
                  y2="17.0925"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.82252"
                  y1="13.1675"
                  x2="19.988"
                  y2="13.1675"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <path
                  d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z"
                  fill="#69C4FF"
                  fillOpacity="0.14"
                />
              </g>
              <defs>
                <filter
                  id="filter0_d_190_157"
                  x="0.255097"
                  y="0.170166"
                  width="24.9498"
                  height="30.5854"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="1.74496" />
                  <feGaussianBlur stdDeviation="0.872482" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_190_157"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_190_157"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </div>
          <div className="panel-button-text">
            <div className="panel-button-desc">
              Click here to follow the complete setup guide for the Tag Manager
              {/* Question not covered?{" "}
              <a
                href="https://portal.code-cube.io/support_request"
                className="support_request_link"
                target="_blank"
              >
                Please get in contact.
              </a> */}
            </div>
          </div>
        </Link>
        <Link
          className="modal-guide-banner "
          style={{ flex: 1 }}
          target="_blank"
          to={clientSideTemplateLink}
        >
          <div className="folder-icon">
            <svg
              width="35"
              height="35"
              viewBox="0 0 31 31"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_d_141_70)">
                <path
                  d="M5.17672 7.1814H8.42156C9.13839 7.1814 9.71949 7.7625 9.71949 8.47933V19.9444C9.71949 20.6613 9.13839 21.2424 8.42156 21.2424H5.17672C4.45989 21.2424 3.87878 20.6613 3.87878 19.9444V8.47933C3.87878 7.7625 4.45989 7.1814 5.17672 7.1814Z"
                  fill="#D8F0FF"
                  stroke="#B0E0FF"
                  strokeWidth="0.648968"
                />
                <line
                  x1="9.58228"
                  y1="14.3395"
                  x2="19.3946"
                  y2="14.3395"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.342291"
                  strokeLinecap="round"
                />
                <line
                  x1="9.51154"
                  y1="11.0043"
                  x2="19.3239"
                  y2="11.0043"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.342291"
                  strokeLinecap="round"
                />
                <line
                  x1="9.58228"
                  y1="16.0509"
                  x2="19.3946"
                  y2="16.0509"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.342291"
                  strokeLinecap="round"
                />
                <line
                  x1="9.51154"
                  y1="12.6017"
                  x2="19.3239"
                  y2="12.6017"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.342291"
                  strokeLinecap="round"
                />
                <path
                  d="M8.62264 0.229834L19.6322 1.8242L19.1171 5.38115C19.072 5.69297 19.2882 5.98235 19.6 6.02751L23.1573 6.54266L20.9206 21.9877C20.8599 22.4068 20.4709 22.6973 20.0519 22.6366L5.6792 20.5553C5.36738 20.5101 5.15121 20.2207 5.19636 19.9089L7.97628 0.712666C8.02144 0.400849 8.31082 0.184678 8.62264 0.229834Z"
                  fill="white"
                />
                <line
                  x1="9.86059"
                  y1="8.60723"
                  x2="19.5716"
                  y2="10.0135"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.342291"
                  strokeLinecap="round"
                />
                <path
                  d="M19.5957 6.05963C19.2839 6.01448 19.0677 5.72509 19.1129 5.41327L19.6327 1.82417L23.1526 6.5747L19.5957 6.05963Z"
                  fill="#69C4FF"
                  fillOpacity="0.14"
                />
                <path
                  d="M25.2865 10.6967H8.13698C7.36822 10.6967 6.68867 11.1963 6.45937 11.93L3.71309 20.7181C3.3594 21.8499 4.20494 23 5.3907 23H22.8149C23.6023 23 24.2937 22.4763 24.5069 21.7182L25.8765 16.8483L26.9785 12.9301C27.2939 11.8089 26.4512 10.6967 25.2865 10.6967Z"
                  fill="#B0E0FF"
                />
              </g>
              <defs>
                <filter
                  id="filter0_d_141_70"
                  x="0.0390809"
                  y="0.223877"
                  width="30.5213"
                  height="29.8066"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="3.51524" />
                  <feGaussianBlur stdDeviation="1.75762" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_141_70"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_141_70"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </div>
          <div className="panel-button-text">
            <div className="panel-button-desc">
              Click here to download the Google Tag Manager client-side template
            </div>
          </div>
        </Link>
      </div>
      <div style={{ position: "relative" }}>
        <h3
          className="modal-category"
          // style={{
          //   display: "flex",
          //   alignItems: "center",
          //   marginBottom: "0.1rem",
          // }}
        >
          Container set-up
          <Tooltip title="Only the container(s) added to this list will be activated for alerting and will show up in the dashboards.">
            <InfoCircleFilled
              size={15}
              className="info-icon"
              style={{ marginLeft: "10px" }}
            />
          </Tooltip>
        </h3>
        <p style={{ lineHeight: "20px", marginBottom: "1rem" }}>
          Add your Google Tag Manager container ID'(s) to the list below.
        </p>
        <div className="list-col-2-add">
          {curGTMContainers?.map((elem, idx) => (
            <div key={idx} className="inputs-row-3-col-single">
              <input
                type="text"
                className="GTM-container"
                placeholder="GTM-XXXXX"
                value={elem?.container || ""}
                onChange={(event) =>
                  handleGTMContainerInputValueChange(idx, event, "gtmID")
                }
              />
              <div className="inputs-row-3-col-single-actions">
                <DashCircle
                  className="color-dark"
                  size={18}
                  onClick={() => handleElemDeleteGTM(idx)}
                  style={{ cursor: "pointer" }}
                />
                <Tooltip 
                  title={trigger === "hover" ? content : ""}
                  overlayInnerStyle={{ minWidth: "320px" }}
                >
                  {idx === curGTMContainers.length - 1 ? (
                    <PlusCircle
                      className="color-dark"
                      size={18}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleElemAddGTM(idx)}
                    />
                  ) : null}
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3
          className="modal-category"
          // style={{
          //   display: "flex",
          //   alignItems: "center",
          //   marginBottom: "0.1rem",
          // }}
        >
          Installation
        </h3>
        <div style={{ padding: "0 0 0 20px", marginBottom: "1rem" }}>
          <ol style={{ paddingLeft: "0", margin: "0", textAlign: "left" }}>
            <li>Download the Code-Cube.io template via the button above.</li>
            <li>
              Import the template in your GTM container and create a new
              monitoring tag.
            </li>
            <li>Copy the "Dataset name" the Code-Cube.io monitoring tag.</li>
          </ol>
        </div>
        <div
          className="d-flex align-items-center"
          style={{ maxWidth: "20rem", gap: "10px" }}
        >
          <input
            className="disabled-input"
            type="text"
            placeholder="Database name"
            value={databaseName || ""}
            readOnly
            ref={inputRefDataset}
          />
          <Tooltip title={isDatasetCopied ? "Copied successfully" : "Copy dataset name"}>
            <button
              type="button"
              onClick={handleCopyDatasetName}
              style={{ border: "none", background: "transparent", padding: 0 }}
              aria-label={isDatasetCopied ? "Copied" : "Copy dataset name"}
            >
              {isDatasetCopied ? (
                <CheckOutlined className="copy-icon" style={{ fontSize: "18px" }} />
              ) : (
                <img
                  className="copy-icon"
                  src={copyIcon}
                  alt="Copy"
                  fill="currentColor"
                />
              )}
            </button>
          </Tooltip>
        </div>
      </div>

      <div>
        <h3
          className="modal-category"
          // style={{
          //   display: "flex",
          //   alignItems: "center",
          //   marginBottom: "0.1rem",
          // }}
        >
          Configure tag names
        </h3>
        <p style={{ lineHeight: "20px", marginBottom: "1rem" }}>
          Tag Monitor shows Tag ID'S by default. Add friendly names to your
          tags. Click "Start bulk editing" to begin.
        </p>
        <Button type="default" onClick={() => navigate("/authorize_user")}>
          start bulk editing
          <ArrowRightOutlined style={{ marginLeft: "8px" }} />
        </Button>
      </div>

      <div style={{ position: "relative" }}>
        <h3
          className="modal-category fw-bold"
          // style={{ lineHeight: "20px", marginBottom: "0.1rem" }}
        >
          Tags to exclude from alerting
        </h3>
        <p style={{ lineHeight: "20px", marginBottom: "1rem" }}>
          Select tags to exclude from receiving alerts. If you want to fully exclude tags, then you can do that from GTM.
        </p>
        <div className="list-col-2-add tag-exclusion-list">
          {Array.isArray(tagsMonitoringExcludeClient) &&
            tagsMonitoringExcludeClient.map((elem, idx) => (
              <div
                key={idx}
                className="inputs-row-3-col-single d-flex flex-row"
                style={{ padding: 0 }}
              >
                <div className="dropdown">
                  <Select
                    style={{ minWidth: "9rem" }}
                    showSearch
                    value={elem?.type || "tag name"}
                    onChange={(value) =>
                      handleExcludedTagInputValue(idx, "type", value)
                    }
                    popupClassName="custom-dropdown"
                  >
                    {excludetagsby.map((option) => (
                      <Select.Option key={option} value={option}>
                        {option}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div className="dropdown-tags">
                  <Select
                    value={elem?.match || "exactly matches"}
                    style={{ minWidth: "9rem" }}
                    showSearch
                    onChange={(value) =>
                      handleExcludedTagInputValue(idx, "match", value)
                    }
                    popupClassName="custom-dropdown"
                  >
                    {matchOptions.map((option) => (
                      <Select.Option key={`${option}-${idx}`} value={option}>
                        {option}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div className="tags-container">
                  <TagValueAutoComplete
                    placeholder={
                      elem?.type === "tag id" ? "tag id" : "tag name"
                    }
                    value={elem?.value || ""}
                    onChange={(val) =>
                      handleExcludedTagInputValue(idx, "value", val)
                    }
                    tagType={elem?.type}
                    matchType={elem?.match}
                    tags={clientTags}
                    loading={tagNamesLoading}
                    error={tagNamesError}
                  />
                </div>
                <div className="inputs-row-3-col-single-actions">
                  <DashCircle
                    className="color-dark"
                    size={18}
                    onClick={() => handleElemDeleteExcludedClientTag(idx)}
                  ></DashCircle>{" "}
                  {idx === tagsMonitoringExcludeClient.length - 1 ? (
                    <PlusCircle
                      className="color-dark"
                      size={18}
                      onClick={handleElemAddExcludedClientTag}
                    ></PlusCircle>
                  ) : null}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Notification(s) Collapsible Section */}
      <div
        onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          marginTop: "24px",
          marginBottom: "16px",
        }}
      >
        {isNotificationsOpen ? (
          <DownOutlined style={{ fontSize: "14px", marginRight: "8px" }} />
        ) : (
          <RightOutlined style={{ fontSize: "14px", marginRight: "8px" }} />
        )}
        <h3 className="modal-category fw-bold" style={{ margin: 0 }}>
          Notification(s)
        </h3>
      </div>

      {isNotificationsOpen && (
        <div
          style={{
            marginTop: "-20px",
            padding: "16px",
            backgroundColor: "#f7fbff",
            borderRadius: "8px",
          }}
        >
          {/* 1. Notification threshold */}
          <div style={{ marginBottom: "16px", position: "relative" }}>
            <h3 className="modal-category fw-bold">
              Notification threshold
              <Tooltip title="The error % represents the percentage of failed tags out of all fired tags. For example: If 100 tags are fired and 10 failed, the error % is 10%. Notifications are sent when the error % meets the selected threshold.">
                <InfoCircleFilled
                  size={15}
                  className="info-icon"
                  style={{ marginLeft: "10px" }}
                />
              </Tooltip>
            </h3>

            <p style={{ lineHeight: "20px", marginBottom: "1rem" }}>
              Choose an error percentage to determine when you should receive
              notifications.
            </p>
            <Select
              style={{ width: "8em" }}
              value={`${curThreshold}%`}
              popupClassName="custom-dropdown"
              onChange={(value) => handleThresholdChange(parseInt(value))}
            >
              {percentages.map((elem) => (
                <Option key={elem} value={`${elem}`}>{`${elem}%`}</Option>
              ))}
            </Select>
          </div>

          {/* <Divider /> */}

          {/* 2. Repeat Alert Sensitivity */}
          <div style={{ position: "relative" }}>
            <h3 className="modal-category fw-bold">
              New notification
              <Tooltip title="Choose when you want to receive new notifications based on error increase. Small changes: Get notified quickly when errors increase slightly. Medium changes: Balanced - recommended for most cases. Big changes: Only get notified when errors increase significantly.">
                <InfoCircleFilled
                  size={15}
                  className="info-icon"
                  style={{ marginLeft: "10px", marginTop: "10px" }}
                />
              </Tooltip>
            </h3>

            <p style={{ lineHeight: "20px", marginBottom: "1rem" }}>
              Send me a new notification when:
            </p>

            {/* 4. Sensitivity Dropdown - Alternative Wording */}
            <Select
              value={alertSensitivity}
              onChange={handleSensitivityChange}
              popupClassName="custom-dropdown"
              style={{ width: "100%", maxWidth: "21rem", marginBottom: "1rem" }}
            >
              <Option value="high">
                Small changes in error % {/* - 5%(abs) & 25%(rel) */}
              </Option>
              <Option value="medium">
                Medium changes in error % - Recommended
                {/* - 10%(abs) & 50%(rel) */}
              </Option>
              <Option value="low">
                Big changes in error % {/* - 20%(abs) & 100%(rel) */}
              </Option>
              {/* <Option value="custom">Custom threshold...</Option> */}
            </Select>

            {/* 5. Custom Fields - Option B Labels */}
            {alertSensitivity === "custom" && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "1.25rem",
                  backgroundColor: "#f2f2f2",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                  borderLeft: "3px solid #6535f5",
                }}
              >
                <Space
                  direction="vertical"
                  style={{ width: "100%" }}
                  size="middle"
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontWeight: "500",
                        fontSize: "14px",
                      }}
                    >
                      Minimum increase (points):
                    </label>
                    <InputNumber
                      min={1}
                      max={100}
                      value={absoluteDeviation}
                      onChange={setAbsoluteDeviation}
                      addonAfter="%"
                      placeholder="10"
                      style={{
                        width: "100%",
                        maxWidth: "200px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      textAlign: "center",
                      fontWeight: "700",
                      fontSize: "13px",
                      color: "#6535f5",
                    }}
                  >
                    AND
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontWeight: "500",
                        fontSize: "14px",
                      }}
                    >
                      Minimum growth (%):
                    </label>
                    <InputNumber
                      min={1}
                      max={1000}
                      value={relativeDeviation}
                      onChange={setRelativeDeviation}
                      addonAfter="%"
                      placeholder="50"
                      style={{
                        width: "100%",
                        maxWidth: "200px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                </Space>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const SSTSettingsModal = ({ context }) => {
  const {
    databaseName,
    SSImplmenetationLink,
    serverSideTemplateLink,
    serverErrorsThreshold,
    setConfigParams,
    setModalSaveStatus,
    gtmContainers,
    serverSideAvailable,
    customNmbrCid,
    subscriptionModel,
    tags_monitoring_exclude_server,
    currentConfig,
  } = useContext(context);

  const navigate = useNavigate();
  const percentages = [1, 5, 10, 25, 50, 100];

  const [curThreshold, setCurThreshold] = useState(serverErrorsThreshold);
  const [configParamsToSave, setConfigParamsToSave] = useState({});
  const [showGuideDiv, setShowGuideDiv] = useState(false);
  const [maxNmbrCids, setMaxNmbrCids] = useState(false);
  const [trigger, setTrigger] = useState("");
  const [showGuideDivGTM, setShowGuideDivGTM] = useState(false);
  const [showGuideDivSensitivity, setShowGuideDivSensitivity] = useState(false);

  const {
    serverTags,
    loading: tagNamesLoading,
    error: tagNamesError,
  } = useTagNameOptions();

  // Alert deviation thresholds state - Server-side
  const [absoluteDeviation, setAbsoluteDeviation] = useState(
    currentConfig?.alert_absolute_deviation_server || 10,
  );
  const [relativeDeviation, setRelativeDeviation] = useState(
    currentConfig?.alert_relative_deviation_server || 50,
  );

  // Determine current sensitivity preset
  const determineAlertSensitivity = (abs, rel) => {
    if (abs === 5 && rel === 25) return "high";
    if (abs === 10 && rel === 50) return "medium";
    if (abs === 25 && rel === 100) return "low";
    return "custom";
  };

  const [alertSensitivity, setAlertSensitivity] = useState(
    determineAlertSensitivity(
      currentConfig?.alert_absolute_deviation_server || 10,
      currentConfig?.alert_relative_deviation_server || 50,
    ),
  );

  // Collapsible notification section state
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const [curGTMContainers, setCurGTMContainers] = useState(
    gtmContainers && gtmContainers.length
      ? gtmContainers
      : [{ type: "server-side" }],
  );

  const excludetagsby = ["tag name", "tag id"];
  const matchOptions = ["exactly matches", "matches regex", "contains"];
  const defaultType = excludetagsby[0];
  const defaultMatch = matchOptions[0];

  const normalizeTag = (tag) => ({
    type: tag?.type || defaultType,
    match: tag?.match || defaultMatch,
    value: tag?.value || "",
  });

  const [tagsMonitoringExcludeServer, setTagsMonitoringExcludeServer] =
    useState(
      Array.isArray(tags_monitoring_exclude_server) &&
        tags_monitoring_exclude_server.length > 0
        ? tags_monitoring_exclude_server.map(normalizeTag)
        : [normalizeTag({})],
    );

  useEffect(() => {
    setTagsMonitoringExcludeServer(
      Array.isArray(tags_monitoring_exclude_server) &&
        tags_monitoring_exclude_server.length > 0
        ? tags_monitoring_exclude_server.map(normalizeTag)
        : [normalizeTag({})],
    );
  }, [tags_monitoring_exclude_server]);

  const inputRefDataset = useRef(null);
  const copyFeedbackTimeoutRef = useRef(null);
  const [isDatasetCopied, setIsDatasetCopied] = useState(false);

  const handleCopyDatasetName = async () => {
    const copied = await copyToClipboard(inputRefDataset);
    if (!copied) return;

    setIsDatasetCopied(true);
    if (copyFeedbackTimeoutRef.current) {
      clearTimeout(copyFeedbackTimeoutRef.current);
    }
    copyFeedbackTimeoutRef.current = setTimeout(
      () => setIsDatasetCopied(false),
      2000,
    );
  };

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const content = (
    <div>
      <p>
        You have reached the maximum number of containers <br></br>
        allowed by your subscription. <br></br>
        To add more containers or upgrade your plan, <br></br>
        please contact us via the
        <a
          target="blank"
          className="tooltip-link"
          style={{ textDecoration: 'none', cursor: 'pointer' }}
          href="https://code-cube.io/contact-us/"
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >website</a>
        or
        <a
          target="blank"
          className="tooltip-link"
          style={{ textDecoration: 'none', cursor: 'pointer' }}
          href="https://portal.code-cube.io/support_request"
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >support</a>
        .
      </p>
    </div>
  );

  useEffect(() => {
    setTagsMonitoringExcludeServer(
      tags_monitoring_exclude_server &&
        tags_monitoring_exclude_server.length > 0
        ? tags_monitoring_exclude_server
        : [normalizeTag({})],
    );
  }, [tags_monitoring_exclude_server]);

  useEffect(() => {
    // Set the maximum number of containers per subscription model
    switch (customNmbrCid > 0) {
      case true:
        setMaxNmbrCids(customNmbrCid);
        break;
      default:
        switch (subscriptionModel) {
          case "essentials":
            setMaxNmbrCids(1);
            break;
          case "advanced":
            setMaxNmbrCids(3);
            break;
          case "premium":
          case "enterprise":
            setMaxNmbrCids(35);
            break;
          default:
            setMaxNmbrCids(1);
        }
    }
  }, [subscriptionModel]);

  useEffect(() => {
    setTrigger(curGTMContainers?.length >= maxNmbrCids ? "hover" : "");
  }, [maxNmbrCids, curGTMContainers]);

  // Sync deviation values with currentConfig and update sensitivity
  useEffect(() => {
    if (currentConfig?.alert_absolute_deviation_server !== undefined) {
      setAbsoluteDeviation(currentConfig.alert_absolute_deviation_server);
    }
    if (currentConfig?.alert_relative_deviation_server !== undefined) {
      setRelativeDeviation(currentConfig.alert_relative_deviation_server);
    }
    if (
      currentConfig?.alert_absolute_deviation_server !== undefined &&
      currentConfig?.alert_relative_deviation_server !== undefined
    ) {
      setAlertSensitivity(
        determineAlertSensitivity(
          currentConfig.alert_absolute_deviation_server,
          currentConfig.alert_relative_deviation_server,
        ),
      );
    }
  }, [currentConfig]);

  const handleSensitivityChange = (value) => {
    setAlertSensitivity(value);
    const presets = {
      high: { absolute: 5, relative: 25 },
      medium: { absolute: 10, relative: 50 },
      low: { absolute: 25, relative: 100 },
    };
    if (presets[value]) {
      setAbsoluteDeviation(presets[value].absolute);
      setRelativeDeviation(presets[value].relative);
    }
  };

  const handleElemDeleteGTM = (idx) => {
    const newGtmContainerList = curGTMContainers.filter(
      (_, index) => index !== idx,
    );
    setCurGTMContainers(
      newGtmContainerList.length
        ? newGtmContainerList
        : [{ type: "server-side" }],
    );
  };
  const handleElemAddGTM = () => {
    if (curGTMContainers?.length < maxNmbrCids) {
      setCurGTMContainers([...curGTMContainers, { type: "server-side" }]);
    }
  };
  const handleGTMContainerInputValueChange = (idx, event, valueType) => {
    const newGTMContainerList = [...curGTMContainers];
    const elem = { ...newGTMContainerList[idx] };

    switch (valueType) {
      case "cidType":
        elem["type"] = event.target.innerText;
        break;
      case "gtmID":
        elem["container"] = event.target.value;
        elem["type"] = "server-side";
        break;
      default:
    }

    newGTMContainerList[idx] = elem;
    setCurGTMContainers(newGTMContainerList);
  };

  const handleElemAddExcludedServerTag = () => {
    setTagsMonitoringExcludeServer((prev) => [...prev, normalizeTag({})]);
  };

  const handleElemDeleteExcludedServerTag = (idx) => {
    setTagsMonitoringExcludeServer((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [normalizeTag({})];
    });
  };

  const handleExcludedTagInputValue = (idx, field, newValue) => {
    const newExcludedElemList = [...tagsMonitoringExcludeServer];
    const elem = { ...newExcludedElemList[idx] };

    switch (field) {
      case "type":
        elem.type = newValue === "tag name" ? "tag name" : "tag id";
        break;
      case "match":
        elem.match = newValue;
        break;
      case "value":
        elem.value = newValue;
        break;
      default:
        break;
    }

    newExcludedElemList[idx] = elem;
    setTagsMonitoringExcludeServer(newExcludedElemList);
  };

  const handleThresholdChange = (elem) => setCurThreshold(elem);

  useEffect(
    () => setCurThreshold(serverErrorsThreshold),
    [serverErrorsThreshold],
  );

  // Sync alert deviation values with currentConfig - Server-side
  const isGTMContainerEmpty = (containerObj) => {
    return !containerObj.container || containerObj.container.trim() === "";
  };

  const filterEmptyGTMContainers = (containers) =>
    containers.filter((container) => !isGTMContainerEmpty(container));

  const isTagExclusionEmpty = (tag) => {
    return !tag.value || tag.value.trim() === "";
  };

  const filterEmptyTagExclusions = (tags) =>
    tags.filter((tag) => !isTagExclusionEmpty(tag));

  useEffect(() => {
    setConfigParamsToSave((prevParams) => ({
      ...prevParams,
      gtm_containers_server: filterEmptyGTMContainers(curGTMContainers),
      tags_monitoring_exclude_server: filterEmptyTagExclusions(
        tagsMonitoringExcludeServer,
      ),
      client_errors_threshold: curThreshold,
      alert_absolute_deviation_server: absoluteDeviation,
      alert_relative_deviation_server: relativeDeviation,
    }));
  }, [
    curGTMContainers,
    tagsMonitoringExcludeServer,
    curThreshold,
    absoluteDeviation,
    relativeDeviation,
  ]);

  useDirtyState(
    {
      gtm: filterEmptyGTMContainers(gtmContainers),
      // Normalize the baseline exactly like the live state (see useState above)
      // so unchanged tags don't read as different shapes.
      tags: filterEmptyTagExclusions(
        Array.isArray(tags_monitoring_exclude_server) &&
          tags_monitoring_exclude_server.length > 0
          ? tags_monitoring_exclude_server.map(normalizeTag)
          : [normalizeTag({})],
      ),
      threshold: Number(serverErrorsThreshold),
      absDev: Number(currentConfig?.alert_absolute_deviation_server || 10),
      relDev: Number(currentConfig?.alert_relative_deviation_server || 50),
    },
    {
      gtm: filterEmptyGTMContainers(curGTMContainers),
      tags: filterEmptyTagExclusions(tagsMonitoringExcludeServer),
      threshold: Number(curThreshold),
      absDev: Number(absoluteDeviation),
      relDev: Number(relativeDeviation),
    },
    setModalSaveStatus,
  );

  useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);

  return (
    <div className="modal-elems-rows">
      <div>
        <h2 className="header-letter-spacing">Server-side error monitoring setup</h2>
      </div>
      <div
        className="container-guide modal-panel-button-row"
        style={{ display: "flex", gap: "1em" }}
      >
        <Link
          className="modal-guide-banner "
          style={{ flex: 1 }}
          target="_blank"
          to={SSImplmenetationLink}
        >
          <div className="file-icon">
            <svg
              width="30"
              height="36"
              viewBox="0 0 30 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_d_190_157)">
                <path
                  d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z"
                  fill="#69C4FF"
                  fillOpacity="0.5"
                />
                <path
                  d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z"
                  fill="#69C4FF"
                  fillOpacity="0.5"
                />
                <path
                  d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z"
                  fill="#DBF1FF"
                />
                <path
                  d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z"
                  fill="#B0E0FF"
                />
                <path
                  d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z"
                  fill="white"
                />
                <line
                  x1="8.82252"
                  y1="9.40248"
                  x2="19.988"
                  y2="9.40248"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.90306"
                  y1="15.1451"
                  x2="20.0685"
                  y2="15.1451"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.82252"
                  y1="11.3499"
                  x2="19.988"
                  y2="11.3499"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.90306"
                  y1="17.0925"
                  x2="20.0685"
                  y2="17.0925"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <line
                  x1="8.82252"
                  y1="13.1675"
                  x2="19.988"
                  y2="13.1675"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.389493"
                  strokeLinecap="round"
                />
                <path
                  d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z"
                  fill="#69C4FF"
                  fillOpacity="0.14"
                />
              </g>
              <defs>
                <filter
                  id="filter0_d_190_157"
                  x="0.255097"
                  y="0.170166"
                  width="24.9498"
                  height="30.5854"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="1.74496" />
                  <feGaussianBlur stdDeviation="0.872482" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_190_157"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_190_157"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </div>
          <div className="panel-button-text">
            <div className="panel-button-desc">
              Click here to follow the complete setup guide for the Tag Manager.
              {/* Question not covered?{" "}
              <a
                href="https://portal.code-cube.io/support_request"
                className="support_request_link"
                target="_blank"
              >
                Please get in contact.
              </a> */}
            </div>
          </div>
        </Link>
        <Link
          className="modal-guide-banner "
          style={{ flex: 1 }}
          target="_blank"
          to={serverSideTemplateLink}
        >
          <div className="folder-icon">
            <svg
              width="35"
              height="35"
              viewBox="0 0 31 31"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_d_141_70)">
                <path
                  d="M5.17672 7.1814H8.42156C9.13839 7.1814 9.71949 7.7625 9.71949 8.47933V19.9444C9.71949 20.6613 9.13839 21.2424 8.42156 21.2424H5.17672C4.45989 21.2424 3.87878 20.6613 3.87878 19.9444V8.47933C3.87878 7.7625 4.45989 7.1814 5.17672 7.1814Z"
                  fill="#D8F0FF"
                  stroke="#B0E0FF"
                  strokeWidth="0.648968"
                />
                <line
                  x1="9.58228"
                  y1="14.3395"
                  x2="19.3946"
                  y2="14.3395"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.342291"
                  strokeLinecap="round"
                />
                <line
                  x1="9.51154"
                  y1="11.0043"
                  x2="19.3239"
                  y2="11.0043"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.342291"
                  strokeLinecap="round"
                />
                <line
                  x1="9.58228"
                  y1="16.0509"
                  x2="19.3946"
                  y2="16.0509"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.342291"
                  strokeLinecap="round"
                />
                <line
                  x1="9.51154"
                  y1="12.6017"
                  x2="19.3239"
                  y2="12.6017"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.342291"
                  strokeLinecap="round"
                />
                <path
                  d="M8.62264 0.229834L19.6322 1.8242L19.1171 5.38115C19.072 5.69297 19.2882 5.98235 19.6 6.02751L23.1573 6.54266L20.9206 21.9877C20.8599 22.4068 20.4709 22.6973 20.0519 22.6366L5.6792 20.5553C5.36738 20.5101 5.15121 20.2207 5.19636 19.9089L7.97628 0.712666C8.02144 0.400849 8.31082 0.184678 8.62264 0.229834Z"
                  fill="white"
                />
                <line
                  x1="9.86059"
                  y1="8.60723"
                  x2="19.5716"
                  y2="10.0135"
                  stroke="#009BFF"
                  strokeOpacity="0.14"
                  strokeWidth="0.342291"
                  strokeLinecap="round"
                />
                <path
                  d="M19.5957 6.05963C19.2839 6.01448 19.0677 5.72509 19.1129 5.41327L19.6327 1.82417L23.1526 6.5747L19.5957 6.05963Z"
                  fill="#69C4FF"
                  fillOpacity="0.14"
                />
                <path
                  d="M25.2865 10.6967H8.13698C7.36822 10.6967 6.68867 11.1963 6.45937 11.93L3.71309 20.7181C3.3594 21.8499 4.20494 23 5.3907 23H22.8149C23.6023 23 24.2937 22.4763 24.5069 21.7182L25.8765 16.8483L26.9785 12.9301C27.2939 11.8089 26.4512 10.6967 25.2865 10.6967Z"
                  fill="#B0E0FF"
                />
              </g>
              <defs>
                <filter
                  id="filter0_d_141_70"
                  x="0.0390809"
                  y="0.223877"
                  width="30.5213"
                  height="29.8066"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="3.51524" />
                  <feGaussianBlur stdDeviation="1.75762" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_141_70"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_141_70"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </div>
          <div className="panel-button-text">
            <div className="panel-button-desc">
              Click here to dwnload the Google Tag Manager server-side template
            </div>
          </div>
        </Link>
      </div>
      <div style={{ position: "relative" }}>
        <h3
          className="modal-category"
          // style={{
          //   display: "flex",
          //   alignItems: "center",
          //   marginBottom: "0.1rem",
          // }}
        >
          Container set-up
          <Tooltip title="Only the container(s) added to this list will be activated for alerting and will show up in the dashboards.">
            <InfoCircleFilled
              size={15}
              className="info-icon"
              // style={{ marginLeft: '20px' }}
            />
          </Tooltip>
        </h3>
        <p style={{ lineHeight: "20px", marginBottom: "1rem" }}>
          Add your Google Tag Manager container ID'(s) to the list below.
        </p>
        <div className="list-col-2-add">
          {curGTMContainers?.map((elem, idx) => (
            <div key={idx} className="inputs-row-3-col-single">
              <input
                type="text"
                className="GTM-container"
                placeholder="GTM-XXXXX"
                value={elem?.container || ""}
                onChange={(event) =>
                  handleGTMContainerInputValueChange(idx, event, "gtmID")
                }
              />
              <div className="inputs-row-3-col-single-actions">
                <DashCircle
                  className="color-dark"
                  size={18}
                  onClick={() => handleElemDeleteGTM(idx)}
                  style={{ cursor: "pointer" }}
                />
                <Tooltip 
                  title={trigger === "hover" ? content : ""}
                  overlayInnerStyle={{ maxWidth: '400px' }}
                >
                  {idx === curGTMContainers.length - 1 ? (
                    <PlusCircle
                      className="color-dark"
                      size={18}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleElemAddGTM(idx)}
                    />
                  ) : null}
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3
          className="modal-category"
          // style={{
          //   display: "flex",
          //   alignItems: "center",
          //   marginBottom: "0.1rem",
          // }}
        >
          Installation
        </h3>
        <div style={{ paddingLeft: "20px", marginBottom: "1rem" }}>
          <ol style={{ paddingLeft: "0", margin: "0", textAlign: "left" }}>
            <li>Download the Code-Cube.io template via the button above.</li>
            <li>
              Import the template in your GTM container and create a new
              monitoring tag.
            </li>
            <li>Copy the "Dataset name" the Code-Cube.io monitoring tag.</li>
          </ol>
        </div>
        <div
          className="d-flex align-items-center"
          style={{ maxWidth: "20rem", gap: "10px" }}
        >
          <input
            className="disabled-input"
            type="text"
            placeholder="Database name"
            value={databaseName || ""}
            readOnly
            ref={inputRefDataset}
          />
          <Tooltip title={isDatasetCopied ? "Copied successfully" : "Copy dataset name"}>
            <button
              type="button"
              onClick={handleCopyDatasetName}
              style={{ border: "none", background: "transparent", padding: 0 }}
              aria-label={isDatasetCopied ? "Copied" : "Copy dataset name"}
            >
              {isDatasetCopied ? (
                <CheckOutlined className="copy-icon" style={{ fontSize: "18px" }} />
              ) : (
                <img
                  className="copy-icon"
                  src={copyIcon}
                  alt="Copy"
                />
              )}
            </button>
          </Tooltip>
        </div>
      </div>

      <div>
        <h3
          className="modal-category"
          // style={{
          //   display: "flex",
          //   alignItems: "center",
          //   marginBottom: "0.1rem",
          // }}
        >
          Configure tag names
        </h3>
        <p style={{ lineHeight: "20px", marginBottom: "1rem" }}>
          Tag Monitor shows Tag ID'S by default. Add friendly names to your
          tags. Click "Start bulk editing" to begin.
        </p>
        <Button type="default" onClick={() => navigate("/authorize_user")}>
          start bulk editing{" "}
          <ArrowRightOutlined style={{ marginLeft: "8px" }} />
        </Button>
      </div>

      <div style={{ position: "relative" }}>
        <h3
          className="modal-category fw-bold"
          // style={{ lineHeight: "20px", marginBottom: "0.1rem" }}
        >
          Tags to exclude from alerting
        </h3>
        <p style={{ lineHeight: "20px", marginBottom: "1rem" }}>
          Select tags to exclude from receiving alerts. If you want to fully exclude tags, then you can do that from GTM.
        </p>
        <div className="list-col-2-add tag-exclusion-list">
          {Array.isArray(tagsMonitoringExcludeServer) &&
            tagsMonitoringExcludeServer.map((elem, idx) => (
              <div
                key={idx}
                className="inputs-row-3-col-single d-flex flex-row"
                style={{ padding: 0 }}
              >
                <div className="dropdown">
                  <Select
                    style={{ minWidth: "9rem" }}
                    showSearch
                    value={elem?.type || "tag name"}
                    onChange={(value) =>
                      handleExcludedTagInputValue(idx, "type", value)
                    }
                    popupClassName="custom-dropdown"
                  >
                    {excludetagsby.map((option) => (
                      <Select.Option key={option} value={option}>
                        {option}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Select
                    value={elem?.match || "exactly matches"}
                    style={{ minWidth: "9rem" }}
                    showSearch
                    onChange={(value) =>
                      handleExcludedTagInputValue(idx, "match", value)
                    }
                    popupClassName="custom-dropdown"
                  >
                    {matchOptions.map((option) => (
                      <Select.Option key={`${option}-${idx}`} value={option}>
                        {option}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div className="tags-container">
                  <TagValueAutoComplete
                    placeholder={
                      elem?.type === "tag id" ? "tag id" : "tag name"
                    }
                    value={elem?.value || ""}
                    onChange={(val) =>
                      handleExcludedTagInputValue(idx, "value", val)
                    }
                    tagType={elem?.type}
                    matchType={elem?.match}
                    tags={serverTags}
                    loading={tagNamesLoading}
                    error={tagNamesError}
                  />
                </div>
                <div className="inputs-row-3-col-single-actions">
                  <DashCircle
                    className="color-dark"
                    size={18}
                    onClick={() => handleElemDeleteExcludedServerTag(idx)}
                  ></DashCircle>{" "}
                  {idx === tagsMonitoringExcludeServer.length - 1 ? (
                    <PlusCircle
                      className="color-dark"
                      size={18}
                      onClick={handleElemAddExcludedServerTag}
                    ></PlusCircle>
                  ) : null}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Notification(s) Collapsible Section */}
      <div
        onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          marginTop: "24px",
          marginBottom: "16px",
        }}
      >
        {isNotificationsOpen ? (
          <DownOutlined style={{ fontSize: "14px", marginRight: "8px" }} />
        ) : (
          <RightOutlined style={{ fontSize: "14px", marginRight: "8px" }} />
        )}
        <h3 className="modal-category fw-bold" style={{ margin: 0 }}>
          Notification(s)
        </h3>
      </div>

      {isNotificationsOpen && (
        <div
          style={{
            marginTop: "-20px",
            padding: "16px",
            backgroundColor: "#f7fbff",
            borderRadius: "8px",
          }}
        >
          {/* 1. Notification Threshold */}
          <div style={{ marginBottom: "16px", position: "relative" }}>
            <h3 className="modal-category fw-bold">
              Notification threshold
              <Tooltip title="The error % represents the percentage of failed tags out of all fired tags. For example: If 100 tags are fired and 10 failed, the error % is 10%. Notifications are sent when the error % meets the selected threshold.">
                <InfoCircleFilled
                  size={15}
                  className="info-icon"
                  style={{ marginLeft: "10px" }}
                />
              </Tooltip>
            </h3>

            <p style={{ lineHeight: "20px", marginBottom: "1rem" }}>
              Choose an error percentage to determine when you should receive
              notifications.
            </p>
            <Select
              style={{ width: "8em" }}
              value={`${curThreshold}%`}
              popupClassName="custom-dropdown"
              onChange={(value) => handleThresholdChange(parseInt(value))}
            >
              {percentages.map((elem) => (
                <Option key={elem} value={`${elem}`}>{`${elem}%`}</Option>
              ))}
            </Select>
          </div>

          {/* <Divider /> */}

          {/* 2. Repeat Alert Sensitivity */}
          <div style={{ position: "relative" }}>
            <h3 className="modal-category fw-bold">
              New notification
              <Tooltip title="Choose when you want to receive new notifications based on error increase. Small changes: Get notified quickly when errors increase slightly. Medium changes: Balanced - recommended for most cases. Big changes: Only get notified when errors increase significantly.">
                <InfoCircleFilled
                  size={15}
                  className="info-icon"
                  style={{ marginLeft: "10px", marginTop: "10px" }}
                />
              </Tooltip>
            </h3>

            <p style={{ lineHeight: "20px", marginBottom: "1rem" }}>
              Send me a new notification when:
            </p>

            {/* 4. Sensitivity Dropdown - Alternative Wording */}
            <Select
              value={alertSensitivity}
              onChange={handleSensitivityChange}
              popupClassName="custom-dropdown"
              style={{ width: "100%", maxWidth: "21rem", marginBottom: "1rem" }}
            >
              <Option value="high">
                Small changes in error %{/* - 5%(abs) & 25%(rel) */}
              </Option>
              <Option value="medium">
                Medium changes in error % - Recommended
                {/* - 10%(abs) & 50%(rel)  */}
              </Option>
              <Option value="low">
                Big changes in error %{/* - 20%(abs) & 100%(rel) */}
              </Option>
              {/* <Option value="custom">Custom threshold...</Option> */}
            </Select>

            {/* 5. Custom Fields - Option B Labels */}
            {alertSensitivity === "custom" && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "1.25rem",
                  backgroundColor: "#f2f2f2",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                  borderLeft: "3px solid #6535f5",
                }}
              >
                <Space
                  direction="vertical"
                  style={{ width: "100%" }}
                  size="middle"
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontWeight: "500",
                        fontSize: "14px",
                      }}
                    >
                      Minimum increase (points):
                    </label>
                    <InputNumber
                      min={1}
                      max={100}
                      value={absoluteDeviation}
                      onChange={setAbsoluteDeviation}
                      addonAfter="%"
                      placeholder="10"
                      style={{
                        width: "100%",
                        maxWidth: "200px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      textAlign: "center",
                      fontWeight: "700",
                      fontSize: "13px",
                      color: "#6535f5",
                    }}
                  >
                    AND
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontWeight: "500",
                        fontSize: "14px",
                      }}
                    >
                      Minimum growth (%):
                    </label>
                    <InputNumber
                      min={1}
                      max={1000}
                      value={relativeDeviation}
                      onChange={setRelativeDeviation}
                      addonAfter="%"
                      placeholder="50"
                      style={{
                        width: "100%",
                        maxWidth: "200px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                </Space>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Tag inactivity threshold: how long a matched tag may stay
// silent before we notify. The canonical value is minutes.
const THRESHOLD_PRESETS = [
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
  { value: 240, label: "4 hours" },
  { value: 360, label: "6 hours" },
  { value: 720, label: "12 hours" },
  { value: 1440, label: "24 hours" },
  { value: 2880, label: "48 hours" },
];
const PRESET_MINUTES = THRESHOLD_PRESETS.map((p) => p.value);
const DEFAULT_THRESHOLD_MINUTES = 720;

// Older clients stored a legacy `frequency` token instead of `threshold`.
// This map is used only as a read fallback so their saved config still loads.
const LEGACY_FREQUENCY_MINUTES = {
  hourly: 60,
  every2hours: 120,
  every4hours: 240,
  every12hours: 720,
  daily: 1440,
};

// Resolve a section's threshold in minutes, falling back to a legacy
// `frequency` token for configs saved before the threshold change.
const resolveThresholdMinutes = (section) => {
  const raw = section?.threshold;
  if (typeof raw === "number" && !Number.isNaN(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "" && !Number.isNaN(Number(raw))) {
    return Number(raw);
  }
  return LEGACY_FREQUENCY_MINUTES[section?.frequency] ?? DEFAULT_THRESHOLD_MINUTES;
};

const MODE_LABELS = {
  inactivity: "Inactivity",
  anomaly: "Anomaly",
  both: "Inactivity + Anomaly",
};

// Older configs stored the inactivity mode as "volume", back when Tag Volume was
// the name of the type rather than the group. Read fallback only — saves always
// write "inactivity". Anything unrecognised falls back to "both" so a rule is
// never silently dropped from an array.
const LEGACY_MONITOR_MODES = { volume: "inactivity" };

const resolveMonitorMode = (value) => {
  const mode = LEGACY_MONITOR_MODES[value] ?? value;
  return mode === "inactivity" || mode === "anomaly" || mode === "both"
    ? mode
    : "both";
};

const DEFAULT_SEVERITY = "warning";

// Single source for the offered severities — the picker renders from this list.
// "critical" needs an ~10x spike or a >=90% drop, so choosing it read as the
// product having gone quiet. The backend still stores and accepts it and no
// client has it set, so there is no mapping to maintain: uncomment to offer it.
const SEVERITY_OPTIONS = [
  { label: "All anomalies", value: "info" },
  { label: "Major only", value: "warning" },
  // { label: "Critical only", value: "critical" },
];

// Display name for a rule the user hasn't named. Never stored — an empty
// sectionTitle is what marks a rule as auto-named.
const generateRuleTitle = (section) => {
  const tag = section?.tagDetails?.[0]?.value?.trim();
  const mode = MODE_LABELS[resolveMonitorMode(section?.monitorMode)];
  return tag ? `${tag} · ${mode}` : "New monitoring rule";
};

// The rule title input sizes itself to whatever it is showing. `ch` is only an
// approximation for a proportional bold font, so pad a little and clamp both
// ends: short names keep a usable click target, long ones can't push the row's
// buttons off the header.
const titleInputWidth = (text) =>
  `${Math.min(Math.max((text || "").length + 3, 14), 42)}ch`;

export const TagInactivitySettingsModal = ({ context, onClose }) => {
  const {
    TIImplmenetationLink,
    gtmContainers,
    companyDomains,
    checkFrequency,
    tagsInactivityClientSections,
    tagsInactivityServerSections,
    tagAnomalyClientSections,
    tagAnomalyServerSections,
    anomalyAvailable,
    setConfigParams,
    setModalSaveStatus,
  } = useContext(context);

  // 1. Initialize with an empty default rule
  const defaultEmptyRule = {
    id: 1,
    sectionTitle: "",
    isActive: true,
    containerIDs: [],
    domains: [],
    tagDetails: [
      { tagType: "tag name", matchType: "exactly matches", value: "" },
    ],
    trigger: [{ type: "hostname", matchType: "exactly matches", value: "" }],
    threshold:
      LEGACY_FREQUENCY_MINUTES[checkFrequency] ?? DEFAULT_THRESHOLD_MINUTES,
    customThreshold: false,
    monitorMode: "both",
    severity: DEFAULT_SEVERITY,
  };

  const [configSections, setConfigSections] = useState([defaultEmptyRule]);
  const [configParamsToSave, setConfigParamsToSave] = useState({});
  const [curCompanyDomains, setCurCompanyDomains] = useState(
    companyDomains || [],
  );
  const [showGuideDiv, setShowGuideDiv] = useState(false);
  const [showGuideDivGTM, setShowGuideDivGTM] = useState(false);
  const [showGuideDivTD, setShowGuideDivTD] = useState(false);
  const [showGuideDivTrigger, setShowGuideDivTrigger] = useState(false);
  const [showGuideDivThreshold, setShowGuideDivThreshold] = useState(false);
  const [showSamplingInfo, setShowSamplingInfo] = useState(false);
  // Rule panels start collapsed; only newly added/duplicated rules open.
  const [openPanels, setOpenPanels] = useState([]);

  // Real tags this company has fired, used to suggest values and to warn about
  // a rule that would silently never match. Kept split by side so a rule only
  // sees tags that exist on its selected container type.
  const {
    clientTags,
    serverTags,
    loading: tagNamesLoading,
    error: tagNamesError,
  } = useTagNameOptions();

  // Memoised so every rule row shares one array reference — the field
  // recomputes its suggestions whenever this changes identity.
  const bothSidesTags = useMemo(
    () => [...clientTags, ...serverTags],
    [clientTags, serverTags],
  );

  // Tags for one rule, scoped to the side(s) of its containers. Before any
  // container is chosen, offer both so the field isn't empty.
  const tagsForSection = (section) => {
    const types = new Set(
      (section.containerIDs || []).map((c) => c?.type).filter(Boolean),
    );
    const hasClient = types.has("client-side");
    const hasServer = types.has("server-side");
    if (hasClient && !hasServer) return clientTags;
    if (hasServer && !hasClient) return serverTags;
    return bothSidesTags;
  };
  const originalConfigSectionsRef = useRef([]);
  const initialConfigSectionsRef = useRef([]);
  const initialCompanyDomainsRef = useRef(companyDomains || []);
  const nextSectionIdRef = useRef(2);

  useEffect(() => {
    setCurCompanyDomains(companyDomains || []);
  }, [companyDomains]);

  useEffect(() => {
    // Combine the inactivity (tags_inactivity) and anomaly (tag_anomaly) arrays
    // back into unified rules. A rule present in both an inactivity and an anomaly
    // array (same id + side) is reconstructed as monitorMode "both".
    const incoming = [
      ...tagsInactivityClientSections.map((s) => ({ s, side: "client-side", source: "inactivity" })),
      ...tagsInactivityServerSections.map((s) => ({ s, side: "server-side", source: "inactivity" })),
      ...(tagAnomalyClientSections || []).map((s) => ({ s, side: "client-side", source: "anomaly" })),
      ...(tagAnomalyServerSections || []).map((s) => ({ s, side: "server-side", source: "anomaly" })),
    ];

    const byKey = new Map();
    incoming.forEach(({ s, side, source }, i) => {
      // Auto-named rules persist an empty title, so fall back to the index
      // rather than letting several id-less rules collapse onto one key.
      const key = `${s.id ?? (s.sectionTitle || `idx-${i}`)}-${side}`;
      const containerIDs =
        s.containerIDs?.map((c) => ({ ...c, type: c.type || side })) ?? [];
      const existing = byKey.get(key);
      if (!existing) {
        const minutes = resolveThresholdMinutes(s);
        byKey.set(key, {
          ...s,
          containerIDs,
          monitorMode: source,
          threshold: minutes,
          customThreshold: !PRESET_MINUTES.includes(minutes),
        });
      } else {
        // The threshold only ever comes off the inactivity side of a "both" rule.
        const minutes =
          source === "inactivity"
            ? resolveThresholdMinutes(s)
            : existing.threshold;
        byKey.set(key, {
          ...existing,
          monitorMode: "both",
          threshold: minutes,
          customThreshold: !PRESET_MINUTES.includes(minutes),
          severity: source === "anomaly" ? s.severity : existing.severity,
        });
      }
    });

    const mergedSections = Array.from(byKey.values());

    if (mergedSections.length > 0) {
      // Ensure every rule has a stable id (legacy configs may lack one).
      let maxId = mergedSections.reduce((max, s) => (s.id > max ? s.id : max), 0);
      mergedSections.forEach((s) => {
        if (!s.id) s.id = ++maxId;
      });
      setConfigSections(mergedSections);
      originalConfigSectionsRef.current = mergedSections;
      initialConfigSectionsRef.current = mergedSections;
      initialCompanyDomainsRef.current = companyDomains || [];
      nextSectionIdRef.current = maxId + 1;
    } else {
      setConfigSections([defaultEmptyRule]);
      originalConfigSectionsRef.current = [defaultEmptyRule];
      initialConfigSectionsRef.current = [defaultEmptyRule];
      initialCompanyDomainsRef.current = companyDomains || [];
      // Nothing configured yet: a lone collapsed empty rule is a dead end.
      setOpenPanels([`panel-${defaultEmptyRule.id}`]);
    }
  }, [
    gtmContainers,
    companyDomains,
    checkFrequency,
    tagsInactivityClientSections,
    tagsInactivityServerSections,
    tagAnomalyClientSections,
    tagAnomalyServerSections,
  ]);

  useDirtyState(
    {
      sections: initialConfigSectionsRef.current,
      domains: initialCompanyDomainsRef.current,
    },
    { sections: configSections, domains: curCompanyDomains },
    setModalSaveStatus,
  );

  useEffect(() => {
    const mapSection = (section, extra) => ({
      id: section.id,
      sectionTitle: section.sectionTitle,
      isActive: section.isActive,
      monitorMode: resolveMonitorMode(section.monitorMode),
      containerIDs: section.containerIDs.map((c) => ({
        value: c.value,
        label: c.label,
        type: c.type,
      })),
      domains: section.domains,
      tagDetails: section.tagDetails.map((d) => ({
        tagType: d.tagType,
        matchType: d.matchType,
        value: d.value,
      })),
      trigger: section.trigger.map((t) => ({
        type: t.type,
        matchType: t.matchType,
        value: t.value,
      })),
      ...extra,
    });

    // Route rules into arrays by mode; inactivity carries threshold, anomaly
    // carries severity. A "both" rule is emitted into both arrays.
    // side: "client-side" | "server-side"; kind: "inactivity" | "anomaly".
    const buildArray = (side, kind) =>
      configSections
        .filter((section) => {
          const mode = resolveMonitorMode(section.monitorMode);
          return kind === "inactivity"
            ? mode === "inactivity" || mode === "both"
            : mode === "anomaly" || mode === "both";
        })
        .map((section) =>
          mapSection(
            section,
            kind === "inactivity"
              ? { threshold: section.threshold }
              : { severity: section.severity || DEFAULT_SEVERITY },
          ),
        )
        .map((sec) => ({
          ...sec,
          containerIDs: sec.containerIDs.filter((c) => c.type === side),
        }))
        .filter((sec) => sec.containerIDs.length > 0);

    const updatedParams = {
      ...configParamsToSave,
      tags_inactivity_client: { config_sections: buildArray("client-side", "inactivity") },
      tags_inactivity_server: { config_sections: buildArray("server-side", "inactivity") },
      tag_anomaly_client: { config_sections: buildArray("client-side", "anomaly") },
      tag_anomaly_server: { config_sections: buildArray("server-side", "anomaly") },
    };

    const curCompanyDomainsForSave = Array.isArray(curCompanyDomains)
      ? curCompanyDomains.map((domain) => ({ domain: domain.domain || domain }))
      : [];
    updatedParams.company_domains = curCompanyDomainsForSave;

    if (!_.isEqual(updatedParams, configParamsToSave)) {
      setConfigParamsToSave(updatedParams);
    }
  }, [configSections, configParamsToSave, companyDomains]);

  useEffect(() => {
    setConfigParams(configParamsToSave);
  }, [configParamsToSave, setConfigParams]);

  // Reset function to restore original state on modal close without saving
  const resetChanges = () => {
    setConfigSections(originalConfigSectionsRef.current);
    setCurCompanyDomains(initialCompanyDomainsRef.current);
    setModalSaveStatus(false);
  };

  const addConfigSection = () => {
    const newSection = {
      id: nextSectionIdRef.current++,
      sectionTitle: "",
      isActive: true,
      containerIDs: [],
      domains: [],
      tagDetails: [
        { tagType: "tag name", matchType: "exactly matches", value: "" },
      ],
      trigger: [{ type: "hostname", matchType: "exactly matches", value: "" }],
      threshold: DEFAULT_THRESHOLD_MINUTES,
      customThreshold: false,
      monitorMode: "both",
      severity: DEFAULT_SEVERITY,
    };
    setConfigSections([...configSections, newSection]);
    setOpenPanels((prev) => [...prev, `panel-${newSection.id}`]);
  };

  const updateSection = (index, key, value) => {
    const updated = configSections.map((s, i) =>
      i === index ? { ...s, [key]: value } : s
    );
    setConfigSections(updated);
  };

  const updateThreshold = (index, selectValue) => {
    const updated = configSections.map((s, i) => {
      if (i !== index) return s;
      if (selectValue === "custom") {
        return { ...s, customThreshold: true };
      }
      return { ...s, customThreshold: false, threshold: selectValue };
    });
    setConfigSections(updated);
  };

  // Combine the days/hours/minutes custom inputs into a single minute value.
  // The scheduler only runs hourly, so anything under 60 minutes cannot be
  // honoured: clamp back up to 1 hour and say why. `key` stops the toast
  // stacking while they type.
  const updateCustomThreshold = (index, days, hours, minutes) => {
    const requested =
      (Number(days) || 0) * 1440 +
      (Number(hours) || 0) * 60 +
      (Number(minutes) || 0);
    if (requested < DEFAULT_THRESHOLD_MINUTES) {
      message.warning({
        key: "threshold-minimum",
        content:
          "1 hour is the shortest period we can monitor",
      });
    }
    const total = Math.max(DEFAULT_THRESHOLD_MINUTES, requested);
    const updated = configSections.map((s, i) =>
      i === index ? { ...s, threshold: total } : s
    );
    setConfigSections(updated);
  };

  const updateTagDetails = (index, key, value) => {
    const updated = configSections.map((s, i) => {
      if (i !== index) return s;
      return { ...s, tagDetails: [{ ...s.tagDetails[0], [key]: value }] };
    });
    setConfigSections(updated);
  };

  const updateTrigger = (index, key, value) => {
    const updated = configSections.map((s, i) => {
      if (i !== index) return s;
      return { ...s, trigger: [{ ...s.trigger[0], [key]: value }] };
    });
    setConfigSections(updated);
  };

  const duplicateSection = (index) => {
    // A copy is explicitly named so it stays distinguishable from its source,
    // which an auto-named rule would not be (both resolve to the same string).
    const newSection = {
      ...configSections[index],
      id: nextSectionIdRef.current++,
      sectionTitle: `${
        configSections[index].sectionTitle ||
        generateRuleTitle(configSections[index])
      } (Copy)`,
    };
    setConfigSections([...configSections, newSection]);
    setOpenPanels((prev) => [...prev, `panel-${newSection.id}`]);
  };

  const removeSection = (index) => {
    if (configSections.length === 1) {
      const resetSection = {
        ...configSections[0],
        containerIDs: [],
        domains: [],
        tagDetails: [{ tagType: "", matchType: "", value: "" }],
        trigger: [{ type: "", matchType: "", value: "" }],
        threshold:
          LEGACY_FREQUENCY_MINUTES[checkFrequency] ?? DEFAULT_THRESHOLD_MINUTES,
        customThreshold: false,
      };
      setConfigSections([resetSection]);
    } else {
      setConfigSections(configSections.filter((_, i) => i !== index));
    }
  };

  const toggleSectionActive = (index) => {
    const updated = configSections.map((s, i) =>
      i === index ? { ...s, isActive: !s.isActive } : s
    );
    setConfigSections(updated);
  };

  const containerOptions = useMemo(() => {
    const clientOptions = Array.isArray(gtmContainers?.client)
      ? gtmContainers.client
          .filter((c) => c.container)
          .map((c, idx) => ({
            label: `${c.container} (client)`,
            value: c.container,
            type: "client-side",
            key: `client-${idx}`,
          }))
      : [];
    const serverOptions = Array.isArray(gtmContainers?.server)
      ? gtmContainers.server
          .filter((c) => c.container)
          .map((c, idx) => ({
            label: `${c.container} (server)`,
            value: c.container,
            type: "server-side",
            key: `server-${idx}`,
          }))
      : [];
    return [...clientOptions, ...serverOptions];
  }, [gtmContainers]);

  return (
    <div className="modal-elems-rows">
      <h2 className="header-letter-spacing">Tag Volume alerting</h2>
      <div>
        <div className="container-guide modal-panel-button-row">
          <Link
            className="modal-guide-banner "
            target="_blank"
            to={TIImplmenetationLink}
          >
            <div className="file-icon">
              <svg
                width="30"
                height="36"
                viewBox="0 0 30 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g filter="url(#filter0_d_190_157)">
                  <path
                    d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z"
                    fill="#69C4FF"
                    fillOpacity="0.5"
                  />
                  <path
                    d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z"
                    fill="#69C4FF"
                    fillOpacity="0.5"
                  />
                  <path
                    d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z"
                    fill="#DBF1FF"
                  />
                  <path
                    d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z"
                    fill="#B0E0FF"
                  />
                  <path
                    d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z"
                    fill="white"
                  />
                  <line
                    x1="8.82252"
                    y1="9.40248"
                    x2="19.988"
                    y2="9.40248"
                    stroke="#009BFF"
                    strokeOpacity="0.14"
                    strokeWidth="0.389493"
                    strokeLinecap="round"
                  />
                  <line
                    x1="8.90306"
                    y1="15.1451"
                    x2="20.0685"
                    y2="15.1451"
                    stroke="#009BFF"
                    strokeOpacity="0.14"
                    strokeWidth="0.389493"
                    strokeLinecap="round"
                  />
                  <line
                    x1="8.82252"
                    y1="11.3499"
                    x2="19.988"
                    y2="11.3499"
                    stroke="#009BFF"
                    strokeOpacity="0.14"
                    strokeWidth="0.389493"
                    strokeLinecap="round"
                  />
                  <line
                    x1="8.90306"
                    y1="17.0925"
                    x2="20.0685"
                    y2="17.0925"
                    stroke="#009BFF"
                    strokeOpacity="0.14"
                    strokeWidth="0.389493"
                    strokeLinecap="round"
                  />
                  <line
                    x1="8.82252"
                    y1="13.1675"
                    x2="19.988"
                    y2="13.1675"
                    stroke="#009BFF"
                    strokeOpacity="0.14"
                    strokeWidth="0.389493"
                    strokeLinecap="round"
                  />
                  <path
                    d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z"
                    fill="#69C4FF"
                    fillOpacity="0.14"
                  />
                </g>
                <defs>
                  <filter
                    id="filter0_d_190_157"
                    x="0.255097"
                    y="0.170166"
                    width="24.9498"
                    height="30.5854"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dy="1.74496" />
                    <feGaussianBlur stdDeviation="0.872482" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="BackgroundImageFix"
                      result="effect1_dropShadow_190_157"
                    />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="effect1_dropShadow_190_157"
                      result="shape"
                    />
                  </filter>
                </defs>
              </svg>
            </div>
            <div className="panel-button-text">
              <div className="panel-button-desc">
                Click here to follow the complete setup guide for the optimize
                tag inactivity configuration.
                <br />
                Question not covered?{" "}
                <SupportRequestLink />
              </div>
            </div>
          </Link>
        </div>
        {/* <div style={{ marginTop: '15px' }}>
          <p style={{ margin: 0 }}>
            Set up Tag volume monitoring via the rules below.
            Need help? Check our {" "}
            <Link target="_blank" to={TIImplmenetationLink}>
              detailed guide
            </Link>.
          </p>
        </div> */}
      </div>

      <Alert
        type="info"
        showIcon
        style={{
          borderRadius: "8px",
          lineHeight: 1.6,
        }}
        message={
          <div style={{ width: "100%", fontSize: "14px" }}>
            <span
              style={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.5rem",
              }}
            >
              Tag volume is based on sampled data
              <Button
                type="link"
                size="small"
                onClick={() => setShowSamplingInfo((prev) => !prev)}
                aria-expanded={showSamplingInfo}
                aria-label={
                  showSamplingInfo
                    ? "Hide sampling details"
                    : "Show sampling details"
                }
                style={{
                  height: "auto",
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  flexShrink: 0,
                  fontSize: "11px",
                }}
                icon={
                  <DownOutlined
                    style={{
                      fontSize: "11px",
                      transition: "transform 0.2s ease",
                      transform: showSamplingInfo
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                  />
                }
              />
            </span>
            {showSamplingInfo && (
              <div style={{ marginTop: "0.75rem", fontSize: "14px" }}>
                <p style={{ marginBottom: "0.5rem" }}>
                  Tag Monitor uses sampling rather than tracking every hit. Volume metrics may vary from GA4 or other tools, and low-volume tags may briefly appear inactive.
                </p>
                <p style={{ marginBottom: 0 }}>
                  To improve accuracy, you can increase your sampling rate.
                  See the{" "}
                  <Link target="_blank" to={TIImplmenetationLink}>
                    documentation
                  </Link>{" "}
                  for more information.
                </p>
              </div>
            )}
          </div>
        }
      />

      <div className="modal-elems-rows-volume">
        {configSections.map((section, index) => {
          const uniqueKey = section.id ?? `section-${index}`;
          const itemsForSection = [
            {
              key: `panel-${uniqueKey}`,
              label: (
                <div className="tag-volume-section">
                  <Input
                    value={section.sectionTitle || ""}
                    onChange={(e) =>
                      updateSection(index, "sectionTitle", e.target.value)
                    }
                    onClick={(e) => e.stopPropagation()}
                    placeholder={generateRuleTitle(section)}
                    className="tag-volume-section-title"
                    style={{
                      width: titleInputWidth(
                        section.sectionTitle || generateRuleTitle(section),
                      ),
                    }}
                  />
                  <div className="tag-volume-section-buttons">
                    <Button
                      type="text"
                      className="tag-volume-duplicate-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateSection(index);
                      }}
                      icon={
                        <img
                          src={require("../../assets/copy-icon.svg").default}
                          alt="Copy"
                          style={{ width: 16, height: 16 }}
                        />
                      }
                    />
                    <Button
                      type="text"
                      className="tag-volume-activate-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSectionActive(index);
                      }}
                      icon={
                        section.isActive ? (
                          <PauseCircleOutlined />
                        ) : (
                          <PlayCircleOutlined />
                        )
                      }
                    />
                    <Popconfirm
                      title="Are you sure you want to delete this rule?"
                      onConfirm={() => removeSection(index)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        type="text"
                        className="tag-volume-delete-btn"
                        onClick={(e) => e.stopPropagation()}
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  </div>
                </div>
              ),
              children: (
                <>
                  {/* Container and Domain selection */}
                  <div className="container-domain-selection">
                    <div>
                      <h3
                        className="modal-category fw-bold mb-2"
                        style={{ fontSize: "14px" }}
                      >
                        Container ID
                        <Tooltip title="Select the GTM container where the rule should apply. Only the ID's added to the configurations are shown.">
                          <InfoCircleFilled className="info-icon" style={{ marginLeft: "10px" }} />
                        </Tooltip>
                      </h3>

                      <Select
                        mode="multiple"
                        placeholder="GTM-XXXX(client)"
                        labelInValue
                        maxTagCount={1}
                        maxTagTextLength="responsive"
                        maxTagPlaceholder={() => "..."}
                        className="container-select"
                        options={containerOptions}
                        popupClassName="custom-dropdown"
                        value={
                          Array.isArray(section.containerIDs)
                            ? section.containerIDs.map((obj, idx) => ({
                                key: `${obj.value}-${idx}`,
                                label: obj.label,
                                value: obj.value,
                              }))
                            : []
                        }
                        onChange={(selectedItems, optionObjects) => {
                          const newContainers = optionObjects.map((opt) => ({
                            value: opt.value,
                            label: opt.label,
                            type: opt.type,
                          }));
                          updateSection(index, "containerIDs", newContainers);
                        }}
                        tagRender={(props) => {
                          const { label, closable, onClose } = props;
                          return (
                            <span
                              style={{
                                backgroundColor: "#DBF1FF",
                                border: "1px solid #DBF1FF",
                                borderRadius: "2px",
                                padding: "0 7px",
                                margin: "2px",
                                display: "inline-block",
                              }}
                            >
                              {label}
                              {closable && (
                                <span
                                  onClick={onClose}
                                  style={{
                                    marginLeft: "4px",
                                    cursor: "pointer",
                                  }}
                                >
                                  ×
                                </span>
                              )}
                            </span>
                          );
                        }}
                      />
                    </div>

                    <div className="">
                      <h3
                        className="modal-category mb-2"
                        style={{ fontSize: "14px" }}
                      >
                        Domain
                        <Tooltip title="Specify which domains to monitor. Only the domains added to the general settings are available.">
                          <InfoCircleFilled className="info-icon" style={{ marginLeft: "10px" }} />
                        </Tooltip>
                      </h3>
                      <Select
                        mode="multiple"
                        placeholder="code-cube.io"
                        maxTagCount={1}
                        maxTagTextLength={20}
                        className="domain-select"
                        popupClassName="custom-dropdown"
                        options={
                          Array.isArray(companyDomains)
                            ? companyDomains.map((domain) => ({
                                value: domain.domain,
                                label: domain.domain,
                              }))
                            : []
                        }
                        value={section.domains}
                        onChange={(value) =>
                          updateSection(index, "domains", value)
                        }
                        tagRender={(props) => {
                          const { label, closable, onClose } = props;
                          return (
                            <span
                              style={{
                                backgroundColor: "#DBF1FF",
                                border: "1px solid #DBF1FF",
                                borderRadius: "2px",
                                padding: "0 7px",
                                margin: "2px",
                                display: "inline-block",
                              }}
                            >
                              {label}
                              {closable && (
                                <span
                                  onClick={onClose}
                                  style={{
                                    marginLeft: "4px",
                                    cursor: "pointer",
                                  }}
                                >
                                  ×
                                </span>
                              )}
                            </span>
                          );
                        }}
                      />
                    </div>
                  </div>
                  {/* Tag details */}
                  <div
                    className="tag-details-container"
                    style={{ position: "relative", marginBottom: 0 }}
                  >
                    <h3
                      className="modal-category mb-2"
                      style={{ fontSize: "14px" }}
                    >
                      Tag details
                      <Tooltip title="Enter the tag name or ID to monitor.">
                        <InfoCircleFilled className="info-icon" style={{ marginLeft: "10px" }} />
                      </Tooltip>
                    </h3>
                    <div className="tag-details-inputs">
                      <div className="tag-details-selects">
                        <Select
                          className="tag-details-select"
                          placeholder="tag name"
                          value={section.tagDetails[0].tagType}
                          onChange={(val) =>
                            updateTagDetails(index, "tagType", val)
                          }
                          popupClassName="custom-dropdown"
                          options={[
                            { value: "tag name", label: "tag name" },
                            { value: "tag ID", label: "tag ID" },
                          ]}
                        />
                        <Select
                          className="tag-details-select"
                          placeholder="Match Type"
                          value={section.tagDetails[0].matchType}
                          onChange={(val) =>
                            updateTagDetails(index, "matchType", val)
                          }
                          popupClassName="custom-dropdown"
                          options={[
                            {
                              value: "exactly matches",
                              label: "exactly matches",
                            },
                            { value: "matches regex", label: "matches regex" },
                            { value: "contains", label: "contains" },
                          ]}
                        />
                      </div>
                      <TagValueAutoComplete
                        placeholder="ga4 - preview"
                        value={section.tagDetails[0].value}
                        onChange={(val) =>
                          updateTagDetails(index, "value", val)
                        }
                        tagType={section.tagDetails[0].tagType}
                        matchType={section.tagDetails[0].matchType}
                        tags={tagsForSection(section)}
                        loading={tagNamesLoading}
                        error={tagNamesError}
                      />
                    </div>
                  </div>
                  {/* Optional trigger */}
                  <div className="tag-details-container">
                    <h3
                      className="modal-category mb-2"
                      style={{ display: "inline", fontSize: "14px" }}
                    >
                      Trigger{" "}
                      <p
                        className="fw-bold"
                        style={{
                          display: "inline",
                          color: "#D3D3D3",
                          fontSize: "10px",
                        }}
                      >
                        (optional)
                      </p>{" "}
                      <Tooltip title="Optional: Specify a page path or URL condition where the tag should be monitored. Leave empty to monitor the tag on all pages. Use this to focus monitoring on specific pages where the tag is expected to fire.">
                        <InfoCircleFilled
                          size={13}
                          className="info-icon-trigger"
                        />
                      </Tooltip>
                    </h3>
                    <div className="tag-details-inputs">
                      <div className="tag-details-selects">
                        <Select
                          className="tag-details-select"
                          placeholder="/tag-monitor"
                          value={section.trigger[0].type}
                          onChange={(val) => updateTrigger(index, "type", val)}
                          popupClassName="custom-dropdown"
                          options={[
                            { value: "page path", label: "page path" },
                            { value: "full url", label: "full url" },
                          ]}
                        />
                        <Select
                          className="tag-details-select"
                          placeholder="Match Type"
                          value={section.trigger[0].matchType}
                          onChange={(val) =>
                            updateTrigger(index, "matchType", val)
                          }
                          popupClassName="custom-dropdown"
                          options={[
                            {
                              value: "exactly matches",
                              label: "exactly matches",
                            },
                            { value: "matches regex", label: "matches regex" },
                            { value: "contains", label: "contains" },
                          ]}
                        />
                      </div>
                      <Input
                        placeholder="/tag-monitor"
                        value={section.trigger[0].value}
                        onChange={(e) =>
                          updateTrigger(index, "value", e.target.value)
                        }
                        style={{ minWidth: "14rem" }}
                      />
                    </div>
                  </div>

                  {/* What to monitor */}
                  <div
                    className="what-to-monitor"
                    style={{
                      position: "relative",
                      marginTop: "22px",
                      paddingTop: "18px",
                      marginBottom: "14px",
                      borderTop: "1px solid #eef2f7",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "7px",
                        fontWeight: 700,
                        fontSize: "14.5px",
                        color: "#23233a",
                        marginBottom: "4px",
                      }}
                    >
                      What to monitor
                      <Tooltip title="Choose tag anomaly detection, tag inactivity monitoring or both for this rule. A monitoring rule always monitors at least one type.">
                        <InfoCircleFilled
                          className="info-icon"
                          style={{ marginLeft: "10px" }}
                        />
                      </Tooltip>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "4px",
                        background: "rgb(242, 242, 242)",
                        borderRadius: "11px",
                        padding: "4px",
                      }}
                    >
                      {[
                        {
                          label: "Anomaly",
                          value: "anomaly",
                          disabled: anomalyAvailable === false,
                        },
                        { label: "Inactivity", value: "inactivity", disabled: false },
                        {
                          label: "Both",
                          value: "both",
                          disabled: anomalyAvailable === false,
                        },
                      ].map((opt) => {
                        const active =
                          resolveMonitorMode(section.monitorMode) === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            disabled={opt.disabled}
                            onClick={() =>
                              updateSection(index, "monitorMode", opt.value)
                            }
                            style={{
                              flex: 1,
                              height: "34px",
                              border: "none",
                              borderRadius: "8px",
                              fontSize: "14px",
                              fontWeight: 600,
                              cursor: opt.disabled ? "not-allowed" : "pointer",
                              background: active
                                ? "#09003d"
                                : "transparent",
                              color: active ? "#fff" : "#6b6b7b",
                              opacity: opt.disabled ? 0.45 : 1,
                              transition: "background .15s, color .15s",
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Inactivity panel */}
                  {resolveMonitorMode(section.monitorMode) !== "anomaly" && (
                    <div style={{ marginBottom: "16px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "10px",
                        }}
                      >
                        <span
                          style={{
                            width: "9px",
                            height: "9px",
                            borderRadius: "50%",
                            background: "#09003d",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#23233a",
                          }}
                        >
                          Tag inactivity threshold
                          <Tooltip title="If the tag stays silent for longer than this period, we send you a notification. Choose a shorter period for business-critical tags to catch issues quickly, and a longer one for low-traffic tags to avoid unnecessary alerts.">
                            <InfoCircleFilled size={13} className="info-icon" />
                          </Tooltip>                        
                        </span>
                      </div>
                      <Space>
                        <Select
                          placeholder="1 hour"
                          value={
                            section.customThreshold ? "custom" : section.threshold
                          }
                          onChange={(value) => updateThreshold(index, value)}
                          style={{ width: "10rem" }}
                          popupClassName="custom-dropdown"
                          options={[
                            ...THRESHOLD_PRESETS,
                            { value: "custom", label: "Custom…" },
                          ]}
                        />
                        {section.customThreshold && (
                          <>
                            <InputNumber
                              min={0}
                              value={Math.floor((section.threshold ?? 0) / 1440)}
                              onChange={(val) =>
                                updateCustomThreshold(
                                  index,
                                  val,
                                  Math.floor(((section.threshold ?? 0) % 1440) / 60),
                                  (section.threshold ?? 0) % 60,
                                )
                              }
                              style={{ width: "5rem" }}
                              addonAfter="d"
                            />
                            <InputNumber
                              min={0}
                              max={23}
                              value={Math.floor(((section.threshold ?? 0) % 1440) / 60)}
                              onChange={(val) =>
                                updateCustomThreshold(
                                  index,
                                  Math.floor((section.threshold ?? 0) / 1440),
                                  val,
                                  (section.threshold ?? 0) % 60,
                                )
                              }
                              style={{ width: "5rem" }}
                              addonAfter="h"
                            />
                            <InputNumber
                              min={0}
                              max={59}
                              value={(section.threshold ?? 0) % 60}
                              onChange={(val) =>
                                updateCustomThreshold(
                                  index,
                                  Math.floor((section.threshold ?? 0) / 1440),
                                  Math.floor(((section.threshold ?? 0) % 1440) / 60),
                                  val,
                                )
                              }
                              style={{ width: "5rem" }}
                              addonAfter="m"
                            />
                          </>
                        )}
                      </Space>
                    </div>
                  )}

                  {/* Anomaly panel */}
                  {resolveMonitorMode(section.monitorMode) !== "inactivity" && (
                    <div style={{ marginBottom: "16px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "10px",
                        }}
                      >
                        <span
                          style={{
                            width: "9px",
                            height: "9px",
                            borderRadius: "50%",
                            background: "#F62064",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#23233a",
                          }}
                        >
                          Alert me on
                          <Tooltip title="'Major only' limits alerts to large spikes and major drops. 'All anomalies' alerts also on minor deviations.">
                            <InfoCircleFilled size={13} className="info-icon" />
                          </Tooltip>
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          background: "rgb(242, 242, 242)",
                          borderRadius: "9px",
                          padding: "3px",
                          maxWidth: "320px",
                        }}
                      >
                        {SEVERITY_OPTIONS.map((opt) => {
                          const active =
                            (section.severity || DEFAULT_SEVERITY) === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() =>
                                updateSection(index, "severity", opt.value)
                              }
                              style={{
                                flex: 1,
                                height: "34px",
                                border: "none",
                                borderRadius: "7px",
                                fontSize: "13px",
                                fontWeight: 600,
                                cursor: "pointer",
                                background: active ? "#fff" : "transparent",
                                color: active ? "#23233a" : "#6b6b7b",
                                boxShadow: active
                                  ? "0 1px 2px rgba(0,0,0,.08)"
                                  : "none",
                                transition: "background .15s, color .15s",
                              }}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ),
            },
          ];

          return (
            <div key={uniqueKey} style={{ width: "100%", marginBottom: "16px" }}>
              <Collapse
                activeKey={
                  openPanels.includes(`panel-${uniqueKey}`)
                    ? [`panel-${uniqueKey}`]
                    : []
                }
                onChange={() =>
                  setOpenPanels((prev) =>
                    prev.includes(`panel-${uniqueKey}`)
                      ? prev.filter((k) => k !== `panel-${uniqueKey}`)
                      : [...prev, `panel-${uniqueKey}`],
                  )
                }
                ghost
                expandIconPosition="end"
                className="custom-collapse"
                expandIcon={({ isActive }) => (
                  <Button
                    type="text"
                    className="collapse-expand-icon"
                    style={{
                      transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s",
                    }}
                  >
                    <ChevronDown size={20} strokeWidth={4} />
                  </Button>
                )}
                items={itemsForSection.map((item) => ({
                  ...item,
                  children: (
                    <div style={{ padding: "10px" }}>{item.children}</div>
                  ),
                }))}
              />
            </div>
          );
        })}

        <Button
          type="dashed"
          onClick={addConfigSection}
          // className="add-rule-button"
        >
          + add monitoring rule
        </Button>
      </div>
    </div>
  );
};

export const CloudMonitoringSettingsModal = ({ context }) => {
  const { cloudRunData, companyInfo, companyId, setModalSaveStatus, setConfigParams,} =
    useContext(context);

  const tagMonitorSubscription = companyInfo["tag_monitor_subscription_model"];
  const googleCloudImplementationDocsLink =
    "https://docs.code-cube.io/tag-monitor/configuration/monitor-cloud-run/";

  const [googleCloudDataForm, setGoogleCloudDataForm] = useState({});
  const [response, setResponse] = useState({});
  const [loadingActive, setloadingActive] = useState(false);
  const [alertData, setAlertData] = useState({});
  const [formData, setFormData] = useState(cloudRunData);

  useEffect(() => {
    const saveData = async () => {
      setloadingActive(true);
      const resp = await ConfigService.saveGoogleCloudData({
        googleCloudDataForm,
        companyId,
      });
      const respData = (await resp?.json()) || resp;
      setResponse(respData);
      setloadingActive(false);
      return respData;
    };

    if (Object.keys(googleCloudDataForm).length > 0) saveData();
  }, [googleCloudDataForm]);

  useEffect(() => {
    setGoogleCloudDataForm({});
    setResponse({});
    setloadingActive(false);
    setAlertData({});
    setFormData(cloudRunData);
  }, [companyId, cloudRunData]);

  useEffect(() => {
    const error = response.error;
    const details = response.details == "None" ? "" : response.details;
    const success = response.success;
    const type = success ? "success" : "error";
    const message = success ? "Successfull implementation!" : "Error occured!";

    setAlertData({
      error: error,
      details: details,
      type: type,
      message: message,
    });
  }, [response]);

  return (
    <div className="modal-elems-rows">
      <div id="cloud-run-div">
        <div>
          <h2 className="header-letter-spacing">Cloud Run monitoring</h2>
        </div>

        <div style={{ marginTop: "15px", marginBottom: "15px" }}>
          <div className="container-guide modal-panel-button-row">
            <Link
              className="modal-guide-banner "
              target="_blank"
              to={googleCloudImplementationDocsLink}
            >
              <div className="file-icon">
                <svg
                  width="30"
                  height="36"
                  viewBox="0 0 30 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#filter0_d_190_157)">
                    <path
                      d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z"
                      fill="#69C4FF"
                      fillOpacity="0.5"
                    />
                    <path
                      d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z"
                      fill="#69C4FF"
                      fillOpacity="0.5"
                    />
                    <path
                      d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z"
                      fill="#DBF1FF"
                    />
                    <path
                      d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z"
                      fill="#B0E0FF"
                    />
                    <path
                      d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z"
                      fill="white"
                    />
                    <line
                      x1="8.82252"
                      y1="9.40248"
                      x2="19.988"
                      y2="9.40248"
                      stroke="#009BFF"
                      strokeOpacity="0.14"
                      strokeWidth="0.389493"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8.90306"
                      y1="15.1451"
                      x2="20.0685"
                      y2="15.1451"
                      stroke="#009BFF"
                      strokeOpacity="0.14"
                      strokeWidth="0.389493"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8.82252"
                      y1="11.3499"
                      x2="19.988"
                      y2="11.3499"
                      stroke="#009BFF"
                      strokeOpacity="0.14"
                      strokeWidth="0.389493"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8.90306"
                      y1="17.0925"
                      x2="20.0685"
                      y2="17.0925"
                      stroke="#009BFF"
                      strokeOpacity="0.14"
                      strokeWidth="0.389493"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8.82252"
                      y1="13.1675"
                      x2="19.988"
                      y2="13.1675"
                      stroke="#009BFF"
                      strokeOpacity="0.14"
                      strokeWidth="0.389493"
                      strokeLinecap="round"
                    />
                    <path
                      d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z"
                      fill="#69C4FF"
                      fillOpacity="0.14"
                    />
                  </g>
                  <defs>
                    <filter
                      id="filter0_d_190_157"
                      x="0.255097"
                      y="0.170166"
                      width="24.9498"
                      height="30.5854"
                      filterUnits="userSpaceOnUse"
                      colorInterpolationFilters="sRGB"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dy="1.74496" />
                      <feGaussianBlur stdDeviation="0.872482" />
                      <feComposite in2="hardAlpha" operator="out" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_190_157"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_190_157"
                        result="shape"
                      />
                    </filter>
                  </defs>
                </svg>
              </div>
              <div className="panel-button-text">
                <div className="panel-button-desc">
                  Click here to follow the complete setup guide for the Google
                  Cloud Run.
                  <br />
                  Question not covered?{" "}
                  <SupportRequestLink />
                </div>
              </div>
            </Link>
          </div>

          <div style={{ marginTop: "15px" }}>
            <p style={{ margin: 0 }}>
              Enable advanced monitoring for your Google Tag Manager server
              hosted on Cloud Run via the three steps below. Enable real-time
              monitoring of your Cloud Run instances or receive details logs for
              errors encountered in your server-side tag management container.
            </p>
          </div>
        </div>

        <GoogleCloudConfigForm
          cloudRunData={formData}
          saveFunction={setGoogleCloudDataForm}
          tagMonitorSubscription={tagMonitorSubscription}
          setModalSaveStatus={setModalSaveStatus}
          setConfigParams={setConfigParams}
          companyId={companyId}
        />

        {loadingActive && (
          <Flex align="center" gap="middle">
            <Spin
              indicator={
                <LoadingOutlined
                  style={{ fontSize: 48, marginBottom: "1rem" }}
                  spin
                />
              }
            />
          </Flex>
        )}

        {Object.keys(response).length > 0 && !loadingActive && (
          <Alert
            message={alertData.message}
            description={alertData.details}
            type={alertData.type}
          />
        )}
      </div>
    </div>
  );
};
